// @flow

import React, {type Node, useRef} from 'react';
import styled, { css } from 'styled-components';

import spinnerImageSrc from './spinner.svg';
import externalLinkImageSrc from './externallink.svg';
import { useSubmitting } from '@tectonics/prs/submitting';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { Button } from '../../../atoms';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { theme } from '@tectonics/look-and-feel/uber-v2/theme';

export function PlaceOrderButtonPisp(): Node {
  const submitting = useSubmitting();
  const [selectedBank] = useUserInputValue('selectedPixPispBank');
  const buttonRef = useRef<?HTMLButtonElement>();

  const didNotSelectBank = selectedBank === undefined;

  return (
    <ButtonWrapper>
      <Root ref={buttonRef} disabled={submitting || didNotSelectBank} size="lg" didNotSelectBank={didNotSelectBank} type="primary" data-testid="submit-pisp">
        {submitting && <SpinnerImage src={spinnerImageSrc} alt="loading indicator" />}
        <ButtonContent submitting={submitting} />
      </Root>
    </ButtonWrapper>
  );
}

const ButtonWrapper = styled.div`
  display: flex;
  align-content: center;
  padding-inline: 40px;
`;

const Root = styled(Button)`
  position: relative;
  text-transform: uppercase;
  margin: auto;
  width: 100%;

  ${props => props.didNotSelectBank && css `
    background-color:#DFE3E7;
    border: 1px solid #DFE3E7;
  `};

  ${props => !props.didNotSelectBank && css `
    background-color: ${theme.colors.neutral[900]};
    border: 1px solid ${theme.colors.neutral[900]};
  `};
`;

const SpinnerImage = styled.img`
  height: 100%;
  position: absolute;
  transform: translate(-50%, -25%);
`;

function ButtonContent(props): Node {
  return (
    <ButtonContentWrapper {...props}>
      <span>
        <PadlockIcon />
        <ButtonMessage />
      </span>
    </ButtonContentWrapper>
  );
}

function ButtonMessage(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return <BrazilianButtonMessage />;

    default:
      throw new Error(`PlaceOrderButton message for country: ${country} not found`);
  }
}

function BrazilianButtonMessage(): Node {
  return 'Pagar com Pix';
}

const ButtonContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  ${props => props.submitting && css`
    visibility: hidden;
  `}
`;

const PadlockIcon = styled.i`
  &:before {
    content: "";
    padding: .375em;
    margin-right: .8em;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url(${externalLinkImageSrc});
  }
`;
